import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSidenav } from "@angular/material/sidenav";
import { Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import {
  faBars,
  faChevronDown,
  faFilter,
  faPencilAlt,
  faPlus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { AppError } from "src/app/common/validators/app-error";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { Company } from "src/app/models/Company";
import { ExcelExportType } from "src/app/models/ExcelExport";
import { GroupedStatus } from "src/app/models/GroupedStatus";
import { Tool } from "src/app/models/Tool";
import { ToolGridView } from "src/app/models/ToolGridView";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { StorageService } from "src/app/services/storage.service";

import { ToolService } from "../../services/tool.service";
import { FilterCategory, ScheduleDueType, SortOrder, ViewType } from "./../../models/Enums";
import { ToolFilterCategory } from "./../../models/Filters/ToolFilterCategory";
import { ToolColumn } from "./../../models/ToolColumn";
import { ToolFilter } from "./../../models/ToolFilter";
import { ToolFilterService } from "./../../services/tool-filter.service";
import { ToolGridService } from "./../../services/tool-grid.service";

@Component({
  selector: "app-tools",
  templateUrl: "./tools.component.html",
  styleUrls: ["./tools.component.scss"],
  standalone: false,
})
export class ToolsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("sideFilter") sideFilter: MatSidenav;
  private readonly _subscriptions$: Subscription[] = [];
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;
  readonly viewTypeEnum = ViewType;

  faChevronDown = faChevronDown;
  faFilter = faFilter;
  faPencilAlt = faPencilAlt;
  faPlus = faPlus;
  faSpinner = faSpinner;
  faBars = faBars;
  filterActive: boolean;
  isLoading: boolean;
  tools = new MatTableDataSource<Tool>();
  toolsTotal: number = 0;
  pageSize: number = 20;
  page: number = 1;
  quickSearch: string = "";

  toolsDueServiceCount: number | string;
  toolsDueCalibrationCount: number | string;

  viewType: ToolGridView;
  groupedStatuses: GroupedStatus[];
  columns: ToolColumn[];
  pinnedColumns: ToolColumn[];
  selectedColumns: ToolColumn[] = [];
  firstColumnDropdownOptions: ToolColumn[];
  secondColumnDropdownOptions: ToolColumn[];
  thirdColumnDropdownOptions: ToolColumn[];

  aggregations: ToolFilterCategory[];

  filter: ToolFilter;
  ExcelExportType = ExcelExportType;
  isTableOverflow = false;
  resizeListener: () => void;

  isScreenWidthLessThanSm = false;

  isTouchDevice = false;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    public toolService: ToolService,
    private storage: StorageService,
    public companyService: CompanyService,
    private globalOverlayService: GlobalOverlayService,
    private route: ActivatedRoute,
    public toolGrid: ToolGridService,
    public toolFilterService: ToolFilterService,
    private cd: ChangeDetectorRef,
    private renderer: Renderer2,
  ) {
    this.pageSize = this.storage.restoreGridState()?.pageSize ?? 20;
    this.initDropDownOptions();
    this.checkSelectedColumnsOnMobile();
    this.listenOnScreenResize();
    this.checkScreenWidth();
  }
  private listenOnScreenResize() {
    this.resizeListener = this.renderer.listen(window, "resize", () => {
      this.checkScreenWidth();
      this.checkTableOverflow();
    });
  }

  private checkScreenWidth() {
    this.isScreenWidthLessThanSm = window.innerWidth < 576;
  }

  /** If table is overflowing container set class to higlight sticky columns*/
  private checkTableOverflow() {
    const toolTableContainer = document.getElementById("tool-table");
    if (toolTableContainer) {
      this.isTableOverflow = toolTableContainer.scrollWidth > toolTableContainer.clientWidth;
    }
  }

  private checkSelectedColumnsOnMobile() {
    this.isTouchDevice = matchMedia("(hover: none) and (pointer: coarse)").matches;
    // Set selectedColumns only to two when view is less than 576px == $bp-sm and only for touch devices,
    // because delete column is only possible with hover currently
    if (this.isScreenWidthLessThanSm && this.isTouchDevice && this.selectedColumns.length > 2) {
      this.selectedColumns = this.selectedColumns.slice(0, 2);
      this.toolGrid.saveColumns(this.selectedColumns);
    }
  }

  private initDropDownOptions(): void {
    this.columns = this.toolGrid.getAllColumns();
    this.pinnedColumns = this.columns.filter((x) => x.pinned);
    this.selectedColumns = this.toolGrid.getStoredColumns();
  }

  private ensureCompanySelected(): void {
    // if company is not selected, inform the user!
    // TODO: implement proper global handler in case the company is not selected.
    if (!this.companyService.selectedCompany?.name) {
      throw new AppError(`${ToolsComponent.name}: Please select company before proceeding!`);
    }
  }

  ngOnInit() {
    this._subscriptions$.push(this.onCompanyChange());
    this._subscriptions$.push(this.onFilterChange());
    this._subscriptions$.push(this.onTempFilterChange());

    this.route.params.subscribe(() => {
      this.viewType = new ToolGridView();

      this.ensureCompanySelected();

      this.initFilters();
      this.getTools(true);
    });
  }

  // TODO: Rewrite getters to computed when we migrate to Angular 17+
  getDisplayedColumns(): string[] {
    // Add action column always to the end of the list
    return [...this.selectedColumns.map((x) => x.field), "action"];
  }

  /** We need another array for header row with dropdowns to select content,
   *  value needs to be unique, so using just index */
  getSelectHeaderColumns(): string[] {
    let i = 0;
    return Array.from(Array(this.selectedColumns.length), () => {
      const index = i++;
      return index.toString();
    });
  }

  getSelectHeaderColumnsWithAction(): string[] {
    // Add action column always to the end of the list
    return [...this.getSelectHeaderColumns(), "action_upper_header"];
  }

  getColumnOptions(index: number): ToolColumn[] {
    switch (index) {
      case 0:
        return this.pinnedColumns.filter((x) =>
          this.selectedColumns.filter((y, i) => i !== index).every((y) => y.field !== x.field),
        );
      default:
        return this.columns.filter((x) =>
          this.selectedColumns.filter((y, i) => i !== index).every((y) => y.field !== x.field),
        );
    }
  }

  ngAfterViewInit() {
    this._subscriptions$.push(this.onSideFilterOpenedChange());
    this.checkTableOverflow();
    this.cd.detectChanges();
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.setPage();
    this.storage.setGridState({ pageSize: this.pageSize });
    this.getTools();
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((x) => x.unsubscribe());
    // Remove resize listener
    this.resizeListener();
  }

  initFilters() {
    this.ensureCompanySelected();

    this.filter = this.toolFilterService.get(ViewType.ToolsAll);

    // We need to keep the filter as page might be called with specific filter
    // //* Reset Side Bar filters
    // this.filter.columnFilters = [];
    // this.filter.calibrationDueMonths = 1;
    // this.filter.serviceDueMonths = 1;

    this.resetPage();
    this.toolFilterService.set(this.filter, false);
  }

  addNewColumn() {
    const remainingOptions = this.getColumnOptions(this.selectedColumns.length);

    if (!remainingOptions.length) return;

    // Push first from remaining options to the new column
    this.selectedColumns.push(remainingOptions[0]);
    this.onDropDownChange();
  }

  removeColumn(index: number) {
    this.selectedColumns.splice(index, 1);
    this.onDropDownChange();
  }

  setPage() {
    this.filter.skip = (this.page - 1) * this.pageSize;
    this.filter.take = this.pageSize;
  }

  onSearch() {
    this.page = 1;
    this.setPage();
    this.getTools();
  }

  // TODO: separate API calls/endpoints for items and aggregations
  getTools(withAggregations: boolean = false) {
    this.filter.search = this.quickSearch;
    this.globalOverlayService.show();
    this.toolService
      .getByFilter(this.filter, this.filterActive || withAggregations)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((response) => {
        if (!response) return;

        if (!this.filterActive) {
          this.tools.data = response["items"];
          this.toolsTotal = response["totalCount"];
        }

        if (this.filterActive || withAggregations) {
          this.aggregations = response["aggregations"].map((a) => new ToolFilterCategory(a));
          const scheduleDueFilter = this.aggregations.find(
            (x) => x.id == FilterCategory.ServiceDue,
          )?.options;
          const calibrationCount = scheduleDueFilter.find(
            (x) => x.key === ScheduleDueType.CalibrationDue.toString(),
          )?.count;
          this.toolsDueCalibrationCount = calibrationCount == 0 ? "0" : calibrationCount;

          const serviceCount = scheduleDueFilter.find(
            (x) => x.key === ScheduleDueType.ServiceDue.toString(),
          )?.count;
          this.toolsDueServiceCount = serviceCount == 0 ? "0" : serviceCount;

          // change detection called here in order to call input setter
          // in children components. Otherwise not always setter is called
          // on @Input prop (aggregations) correctly.
          this.cd.detectChanges();
        }
      });
  }

  onCompanyChange(): Subscription {
    return this.companyService.companyChange.subscribe((company: Company) => {
      if (!company?.name) return;
      if (!this.viewType) return;

      this.tools.data = null;
      this.initFilters();
      this.getTools();
    });
  }

  onFilterChange(): Subscription {
    return this.toolFilterService.toolFilterChange.subscribe((filter: ToolFilter) => {
      this.filter = filter;
      this.getTools();
    });
  }

  onTempFilterChange(): Subscription {
    return this.toolFilterService.tempToolFilterChange.subscribe((filter: ToolFilter) => {
      this.filter = filter;
      this.getTools();
    });
  }

  onSideFilterOpenedChange() {
    return this.sideFilter.openedChange.subscribe((isOpened) => {
      this.filterActive = isOpened;
      this.filter = this.toolFilterService.get(ViewType.ToolsAll);
      this.getTools();
    });
  }

  toggleServiceDue() {
    const index = this.filter.serviceDueFilters.indexOf(ScheduleDueType.ServiceDue);

    if (index > -1) this.filter.serviceDueFilters.splice(index, 1);
    else this.filter.serviceDueFilters.splice(index, 0, ScheduleDueType.ServiceDue);

    this.resetPage();

    this.toolFilterService.set(this.filter);
  }

  getServiceDueStatus() {
    return this.filter.serviceDueFilters.includes(ScheduleDueType.ServiceDue);
  }

  toggleCalibrationDue() {
    const index = this.filter.serviceDueFilters.indexOf(ScheduleDueType.CalibrationDue);

    if (index > -1) this.filter.serviceDueFilters.splice(index, 1);
    else this.filter.serviceDueFilters.splice(index, 0, ScheduleDueType.CalibrationDue);

    this.resetPage();
    this.toolFilterService.set(this.filter);
  }

  resetPage() {
    this.page = 1;
    this.setPage();
  }

  getCalibrationDueStatus() {
    return this.filter.serviceDueFilters.includes(ScheduleDueType.CalibrationDue);
  }

  toggleGroupedStatus(status: GroupedStatus) {
    const index = this.filter.statusFilters.indexOf(status.value);

    if (index > -1) this.filter.statusFilters.splice(index, 1);
    else this.filter.statusFilters.splice(index, 0, status.value);

    status.current = !status.current;

    this.resetPage();
    this.toolFilterService.set(this.filter);
  }

  onSort(event: Sort) {
    this.filter.sort = event.active;

    if (event.direction === SortOrder.Default) {
      delete this.filter.asc;
      delete this.filter.sort;
    }

    if (event.direction === SortOrder.Ascending) this.filter.asc = true;
    else if (event.direction === SortOrder.Descending) this.filter.asc = false;

    this.toolFilterService.set(this.filter);
  }

  getColumn(fieldName) {
    return this.columns.find((c) => c.field === fieldName);
  }

  getGroupedStatusLabel(value) {
    return this.toolService.statuses.find((x) => x.value == value)?.label;
  }

  getWorktypeLabel(value) {
    return this.toolService.worktypes.find((x) => x.value === value)?.label;
  }

  clearSideBarFilters() {
    this.filter.columnFilters = null;
    this.filter.serviceDueMonths = 1;
    this.filter.calibrationDueMonths = 1;
    this.filter.serviceDueFilters = [];

    this.toolFilterService.set(this.filter);
  }

  onDropDownChange(): void {
    this.toolGrid.saveColumns(this.selectedColumns);
    // We need to check if table is overflowing container after the columns change
    setTimeout(() => this.checkTableOverflow());
  }

  compareColumnsById(colA: ToolColumn, colB: ToolColumn): boolean {
    return colA && colB && colA.headerTitle === colB.headerTitle;
  }
}
